//@flow
import * as Immutable from 'immutable';
import moment from 'moment-timezone';

import { BreakShift } from 'common/models/flow/BreakShift';
import type { BreakShiftModelType } from 'common/models/flow/BreakShift';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { ShiftInstant } from './ShiftInstant';
import { AddressLatLng } from './AddressLatLng';
import NumberUtils from 'common/utils/NumberUtils';
import { DayOfWeekType } from 'common/enums/DateTimeEnums';
import DateTimeUtils, {
    DateInputFormats,
    TimeStyle
} from 'common/utils/DateTimeUtils';

const translationsNamespace = ['validations', 'entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
const daysList = Object.values(DayOfWeekType);

@validatable
class InternalBreakShift extends BreakShift {
    static fromJS(json: BreakShiftModelType): InternalBreakShift {
        const state: Object = Object.assign({}, json);
        state.startShiftInstant = ShiftInstant.fromJS(state.startShiftInstant);
        state.endShiftInstant = ShiftInstant.fromJS(state.endShiftInstant);
        state.location = state.location
            ? AddressLatLng.fromJS(state.location)
            : state.location;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { location } = this;

        if (location && location.hasValidationErrors()) {
            return location.hasValidationErrors();
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { location } = this;
        if (location) {
            location = location.setAllValidationErrors();
        }
        return this.setLocation(location);
    }

    getBreakDescription(): string {
        const {
            duration,
            startShiftInstant,
            endShiftInstant,
            breakType
        } = this;
        const start = startShiftInstant.time;
        const end = endShiftInstant.time;
        return nsTranslate('entityManagement:breakTooltipDescription', {
            duration: duration ? duration / 60 : 0,
            start: DateTimeUtils.format(
                start,
                {
                    timeStyle: TimeStyle.SHORT
                },
                DateInputFormats.HOUR24_SHORT
            ),
            end: DateTimeUtils.format(
                end,
                {
                    timeStyle: TimeStyle.SHORT
                },
                DateInputFormats.HOUR24_SHORT
            ),
            breakType: breakType.toLowerCase()
        });
    }

    getBreakWithOffsetDescription(
        slotFor: $Values<typeof DayOfWeekType>
    ): string {
        const {
            duration,
            startShiftInstant: { time: startTime, day: startDay },
            endShiftInstant: { time: endTime, day: endDay },
            breakType
        } = this;
        const startDayIndex = (startDay - 1 + daysList.indexOf(slotFor)) % 7;
        const endDayIndex = (endDay - 1 + daysList.indexOf(slotFor)) % 7;
        return nsTranslate(
            'entityManagement:breakWithOffesetTooltipDescription',
            {
                duration: duration ? duration / 60 : 0,
                start: DateTimeUtils.format(
                    startTime,
                    {
                        timeStyle: TimeStyle.SHORT
                    },
                    DateInputFormats.HOUR24_SHORT
                ),
                end: DateTimeUtils.format(
                    endTime,
                    {
                        timeStyle: TimeStyle.SHORT
                    },
                    DateInputFormats.HOUR24_SHORT
                ),
                breakType: breakType.toLowerCase(),
                startDay: nsTranslate(
                    `entityManagement:shiftTable.days.${daysList[startDayIndex]}`
                ),
                endDay: nsTranslate(
                    `entityManagement:shiftTable.days.${daysList[endDayIndex]}`
                )
            }
        );
    }

    validateBreakStartWithShift({
        shiftStart,
        startShiftInstant
    }: {
        shiftStart: moment,
        startShiftInstant: ShiftInstant
    }): ?string {
        const { time, day } = startShiftInstant;
        const breakStartMoment = moment(
            time,
            DateTimeFormat.HTML5_FMT_TIME
        ).add(day - 1, 'd');
        if (breakStartMoment.isBefore(shiftStart)) {
            return nsTranslate('breakShift.invalidStartTime');
        }
    }

    validateBreakEndWithShift({
        shiftEnd,
        endShiftInstant
    }: {
        shiftEnd: moment,
        endShiftInstant: ShiftInstant
    }): ?string {
        const { time, day } = endShiftInstant;
        const breakEndMoment = moment(time, DateTimeFormat.HTML5_FMT_TIME).add(
            day - 1,
            'd'
        );
        if (breakEndMoment.isAfter(shiftEnd)) {
            return nsTranslate('breakShift.invalidEndTime');
        }
    }

    @validate
    setStartShiftInstant(slot: ShiftInstant): this {
        return super.setStartShiftInstant(slot);
    }

    validateStartShiftInstant(): string {
        if (
            this.startShiftInstant &&
            this.startShiftInstant.hasValidationErrors()
        ) {
            return nsTranslate('breakShift.invalidStartTime');
        }
    }
    @validate
    setDuration(duration: string): this {
        return super.setDuration(duration);
    }

    validateDuration(): string {
        const { duration } = this;
        if (duration && !NumberUtils.isNumeric(duration)) {
            return nsTranslate('breakShift.breakDurationNotANumber');
        }
    }

    @validate
    setEndShiftInstant(slot: ShiftInstant): this {
        return super.setEndShiftInstant(slot);
    }

    validateEndShiftInstant(): string {
        if (
            this.endShiftInstant &&
            this.endShiftInstant.hasValidationErrors()
        ) {
            return nsTranslate('breakShift.invalidEndTime');
        }
        const startMoment = this.startShiftInstant.getDayAdjustedTimeMoment();
        const endMoment = this.endShiftInstant.getDayAdjustedTimeMoment();
        if (TimeUtils.isAfter(startMoment, endMoment)) {
            return nsTranslate('breakShift.startTimeAfterEndTime');
        }
        const { duration } = this;
        if (duration && endMoment.diff(startMoment, 'seconds') < duration) {
            return nsTranslate('breakShift.breakDurationError');
        }
    }

    get location(): ?AddressLatLng {
        return this._state.get('location');
    }

    get startShiftInstant(): ShiftInstant {
        return this._state.get('startShiftInstant');
    }

    get endShiftInstant(): ShiftInstant {
        return this._state.get('endShiftInstant');
    }
}

export { InternalBreakShift as BreakShift };
